import { DurationPicker, FormGroup, Label, Modal, Select, Toggle, Gap } from '@salecycle/ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  monacoThemes,
  TEMPLATE_EDITOR_DESKTOP_SIZE_STORAGE_ID,
  TEMPLATE_EDITOR_MOBILE_SIZE_STORAGE_ID,
  TEMPLATE_EDITOR_THEME_STORAGE_ID,
  TEMPLATE_EDITOR_WORD_WRAP_STORAGE_ID,
} from '../../constants/index';
import { useTemplateEditorDispatch, useTemplateEditorState } from '../../context/UseTemplateEditorContext';

interface IProps {
  settingsModalOpen: boolean;
  setSettingsModalOpen(isOpen: boolean);
}

export const TemplateEditorSettingsModal = ({ settingsModalOpen, setSettingsModalOpen }: IProps) => {
  const { t } = useTranslation('designs');
  const { editorSettings } = useTemplateEditorState();
  const dispatch = useTemplateEditorDispatch();
  const [theme, setTheme] = useState<string>();
  const [wordWrap, setWordWrap] = useState<boolean>();
  const [mobileSize, setMobileSize] = useState<number>();
  const [desktopSize, setDesktopSize] = useState<number>();

  useEffect(() => {
    setTheme(editorSettings.theme);
    setWordWrap(editorSettings.wordWrap === 'on');
    setMobileSize(Number(editorSettings.mobileSize));
    setDesktopSize(Number(editorSettings.desktopSize));
  }, [editorSettings.desktopSize, editorSettings.mobileSize, editorSettings.theme, editorSettings.wordWrap]);

  const handleSave = () => {
    localStorage.setItem(TEMPLATE_EDITOR_THEME_STORAGE_ID, theme);
    localStorage.setItem(TEMPLATE_EDITOR_WORD_WRAP_STORAGE_ID, wordWrap ? 'on' : 'off');
    localStorage.setItem(TEMPLATE_EDITOR_DESKTOP_SIZE_STORAGE_ID, String(desktopSize));
    localStorage.setItem(TEMPLATE_EDITOR_MOBILE_SIZE_STORAGE_ID, String(mobileSize));

    dispatch({
      type: 'SET_EDITOR_SETTINGS',
      payload: {
        theme,
        wordWrap,
        desktopSize,
        mobileSize,
      },
    });
    setSettingsModalOpen(false);
  };
  const handleClose = () => {
    setSettingsModalOpen(false);
  };

  return (
    <Modal
      heading={t('designGallery.templateEditor.modals.editorSettings.heading')}
      variation="clean"
      isOpen={settingsModalOpen}
      onCancelButtonClicked={handleClose}
      onCloseButtonClicked={handleClose}
      onOkButtonClicked={handleSave}
      okButtonLabel={t('designGallery.templateEditor.modals.editorSettings.okButtonLabel')}
      cancelButtonLabel={t('designGallery.templateEditor.modals.editorSettings.cancelButtonLabel')}
      cancelButtonType="secondary"
      okButtonType="primary"
      size="tall"
    >
      <FormGroup>
        <Label
          tooltipHeading={t('designGallery.templateEditor.modals.editorSettings.wordWrap.tooltipHeading')}
          tooltipDescription={t('designGallery.templateEditor.modals.editorSettings.wordWrap.tooltipDescription')}
          tooltipWidth="300"
          name={t('designGallery.templateEditor.modals.editorSettings.wordWrap.name')}
          variation="form"
        />
        <Gap top={10} />
        <Toggle
          ariaLabel={t('designGallery.templateEditor.modals.editorSettings.wordWrap.toggleAriaLabel')}
          checked={wordWrap}
          onChange={setWordWrap}
        />
      </FormGroup>
      <FormGroup>
        <Select
          ariaLabel={t('designGallery.templateEditor.modals.editorSettings.theme.ariaLabel')}
          onChange={e => setTheme(e.value)}
          items={monacoThemes}
          value={{ value: theme, label: theme }}
          label={t('designGallery.templateEditor.modals.editorSettings.theme.label')}
          hint={t('designGallery.templateEditor.modals.editorSettings.theme.hint')}
        />
      </FormGroup>
      <FormGroup variation="inline">
        <DurationPicker
          value={desktopSize}
          unit={'Pixels'}
          ariaLabel={t('designGallery.templateEditor.modals.editorSettings.desktopView.ariaLabel')}
          unitAriaLabel={t('designGallery.templateEditor.modals.editorSettings.desktopView.unitAriaLabel')}
          onChange={e => setDesktopSize(e)}
          readOnlyUnits={true}
          label={t('designGallery.templateEditor.modals.editorSettings.desktopView.label')}
          hint={t('designGallery.templateEditor.modals.editorSettings.desktopView.hint')}
        />
        <Gap right={10} />
        <DurationPicker
          value={mobileSize}
          unit={'Pixels'}
          ariaLabel={t('designGallery.templateEditor.modals.editorSettings.mobileView.ariaLabel')}
          unitAriaLabel={t('designGallery.templateEditor.modals.editorSettings.mobileView.unitAriaLabel')}
          onChange={e => setMobileSize(e)}
          readOnlyUnits={true}
          label={t('designGallery.templateEditor.modals.editorSettings.mobileView.label')}
          hint={t('designGallery.templateEditor.modals.editorSettings.mobileView.hint')}
        />
      </FormGroup>
    </Modal>
  );
};
